import React, { useState, useEffect } from 'react';
import { ChevronDown, ChevronUp, Trash2, Pencil, Key, UserX, UserCheck, Search, Plus } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../auth/authProvider';
import { usePermissions } from '../hooks/usePermissions';
import {
  Button,
  Badge,
  Card,
  PageHeader,
  Input,
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalTitle,
} from '@license-management/ui';
import { useRoles } from '../api/hooks/rbac';
import { userService } from '../api/services/userService';
import { UserEditModal } from '../components/users';
import { PermissionGate } from '../components/common/PermissionGate';
import ConfirmDialog from '../components/common/ConfirmDialog';
import ActionMenu, { ActionMenuSection } from '../components/common/ActionMenu';

// Permission categories for grouping
const PERMISSION_CATEGORIES: Record<string, string[]> = {
  'Licenses': ['licenses:create', 'licenses:read', 'licenses:update', 'licenses:delete', 'licenses:validate', 'licenses:read_own'],
  'Customers': ['customers:create', 'customers:read', 'customers:update', 'customers:delete'],
  'Users': ['users:create', 'users:read', 'users:update', 'users:delete'],
  'Customer Portal Users': ['customer_users:create', 'customer_users:read', 'customer_users:update', 'customer_users:delete'],
  'Analytics': ['analytics:read', 'analytics:export'],
  'Releases & Updates': ['updates:create', 'updates:read', 'updates:update', 'updates:delete', 'updates:publish', 'updates:rollout', 'updates:check', 'updates:download', 'binaries:upload', 'releases:restore', 'releases:purge'],
  'Machines': ['machines:read', 'machines:annotate'],
  'System': ['system:configure', 'system:manage'],
  'Audit': ['audit:read', 'audit:export', 'audit:configure'],
  'API Keys': ['api_keys:create', 'api_keys:read', 'api_keys:update', 'api_keys:delete', 'api_keys:read_all', 'api_keys:update_all', 'api_keys:delete_all'],
  'Activations': ['activations:read_own', 'activations:create_own', 'activations:delete_own'],
  'Usage': ['usage:report'],
  'Portal': ['portal_licenses:read', 'portal_activations:read', 'portal_releases:read', 'portal_usage:read'],
};

// Helper to format permission action names
const formatAction = (action: string): string => {
  return action.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
};

// Helper to group permissions by category
const groupPermissions = (permissions: Array<{ resource: string; action: string }>): Record<string, string[]> => {
  const grouped: Record<string, string[]> = {};

  permissions.forEach(perm => {
    const permKey = `${perm.resource}:${perm.action}`;

    // Find which category this permission belongs to
    for (const [category, categoryPerms] of Object.entries(PERMISSION_CATEGORIES)) {
      if (categoryPerms.includes(permKey)) {
        if (!grouped[category]) {
          grouped[category] = [];
        }
        grouped[category].push(formatAction(perm.action));
        return;
      }
    }

    // If not found in predefined categories, use resource as category
    const categoryName = perm.resource.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    if (!grouped[categoryName]) {
      grouped[categoryName] = [];
    }
    grouped[categoryName].push(formatAction(perm.action));
  });

  return grouped;
};

interface User {
  id: string;
  email: string;
  name?: string;
  roles: string[];
  status: 'active' | 'inactive';
  createdAt: string;
}

const UserManagement: React.FC = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState<User[]>([]);
  const [newUser, setNewUser] = useState({ email: '', name: '', password: '', roles: [] as string[] });
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  usePermissions();
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [resetPasswordUser, setResetPasswordUser] = useState<{
    id: string;
    oldPassword: string;
    newPassword: string;
    confirmPassword: string;
  } | null>(null);

  // Delete confirmation dialog state
  const [deleteConfirmUser, setDeleteConfirmUser] = useState<User | null>(null);
  
  // Fetch available roles
  const { data: roles, isLoading: rolesLoading, error: rolesError } = useRoles();

  // Get current user's roles from auth context
  const { roles: currentUserRoles } = useAuth();

  // Filter roles based on what the current user can assign
  const getAssignableRoles = (allRoles: typeof roles) => {
    if (!allRoles) return [];

    const currentRoleNames = currentUserRoles.map(r => r.name);
    const isSuperAdmin = currentRoleNames.includes('super_admin');
    const isAdmin = currentRoleNames.includes('admin');

    if (isSuperAdmin) {
      return allRoles; // Can assign all roles
    }

    if (isAdmin) {
      // Can assign all except super_admin
      return allRoles.filter(r => r.name !== 'super_admin');
    }

    return []; // Others shouldn't be able to assign roles
  };

  // Fetch trash count for the recycle bin badge
  const { data: trashData } = useQuery<{ id: string }[]>({
    queryKey: ['users', 'trash'],
    queryFn: async () => {
      try {
        return await userService.getTrashedUsers();
      } catch {
        return [];
      }
    },
  });
  const trashCount = trashData?.length || 0;
  
  // State for expanded permission sections
  const [expandedRoles, setExpandedRoles] = useState<Set<string>>(new Set());

  const toggleRoleExpansion = (roleId: string) => {
    setExpandedRoles(prev => {
      const next = new Set(prev);
      if (next.has(roleId)) {
        next.delete(roleId);
      } else {
        next.add(roleId);
      }
      return next;
    });
  };

  // Fallback roles if API fails (with updated descriptions)
  const fallbackRoles = [
    { roleId: 'role_super_admin', name: 'super_admin', description: 'Full system control: manage users, configure system settings, delete users, purge data', isSystem: true, permissions: [] },
    { roleId: 'role_admin', name: 'admin', description: 'System management: create/update users, manage licenses and customers, view audit logs (cannot delete users or configure system)', isSystem: true, permissions: [] },
    { roleId: 'role_license_manager', name: 'license_manager', description: 'License operations: create, update, and delete licenses; view customers and analytics (read-only customer access)', isSystem: true, permissions: [] },
    { roleId: 'role_binary_manager', name: 'binary_manager', description: 'Release management: upload binaries, manage releases and rollouts, publish updates', isSystem: true, permissions: [] },
    { roleId: 'role_support_agent', name: 'customer_manager', description: 'Customer management: create customers, manage customer information, view licenses and analytics, annotate machines', isSystem: true, permissions: [] },
    { roleId: 'role_viewer', name: 'viewer', description: 'Read-only access: view licenses, customers, and analytics; export reports', isSystem: true, permissions: [] },
  ];
  
  const allRoles = roles || (rolesError ? fallbackRoles : []);
  const availableRoles = getAssignableRoles(allRoles);

  const fetchUsers = async () => {
    try {
      const data = await userService.getUsers(searchTerm);
      setUsers(data);
    } catch (err) {
      setError('Failed to fetch users');
    }
  };

  useEffect(() => {
    fetchUsers();
  }, [searchTerm]);

  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await userService.createUser(newUser);

      setSuccess('User created successfully');
      setNewUser({ email: '', name: '', password: '', roles: [] });
      fetchUsers();
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to create user');
    }
  };

  const handleUpdateUser = async (updatedUser: User) => {
    await userService.updateUser(updatedUser.id, {
      email: updatedUser.email,
      name: updatedUser.name,
      roles: updatedUser.roles,
    });

    setSuccess('User updated successfully');
    setEditingUser(null);
    fetchUsers();
  };

  const handleUserAction = async (userId: string, action: 'deactivate' | 'reactivate' | 'delete') => {
    try {
      if (action === 'deactivate') {
        await userService.deactivateUser(userId);
      } else if (action === 'reactivate') {
        await userService.reactivateUser(userId);
      } else {
        await userService.deleteUser(userId);
      }

      // Handle the success message with the correct verb form
      const actionVerb = action === 'delete' ? 'deleted' : 
                         action === 'deactivate' ? 'deactivated' : 
                         'reactivated';
      
      setSuccess(`User ${actionVerb} successfully`);
      fetchUsers();
    } catch (error) {
      setError(error instanceof Error ? error.message : `Failed to ${action} user`);
    }
  };

  const handleResetPassword = async (userId: string) => {
    if (!resetPasswordUser) {
      setError('Please enter password details');
      return;
    }

    const { newPassword, confirmPassword } = resetPasswordUser;

    if (!newPassword || !confirmPassword) {
      setError('Please fill in all password fields');
      return;
    }

    if (newPassword.length < 6) {
      setError('New password must be at least 6 characters long');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('New passwords do not match');
      return;
    }

    try {
      console.log('Resetting password for user:', userId);
      await userService.resetPassword(userId, { newPassword, confirmPassword });

      setSuccess('Password reset successfully');
      setResetPasswordUser(null);
    } catch (error) {
      console.error('Reset password error:', error);
      setError(error instanceof Error ? error.message : JSON.stringify(error));
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="User Management"
        description="Manage system users and their roles"
        actions={
          <Button
            variant="secondary"
            onClick={() => navigate('/users/trash')}
            leftIcon={<Trash2 className="h-4 w-4" />}
          >
            Recycle Bin
            {trashCount > 0 && (
              <Badge variant="error" size="sm" className="ml-2">
                {trashCount}
              </Badge>
            )}
          </Button>
        }
      />

      {/* Search */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex-1">
          <Input
            placeholder="Search users..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            leftElement={<Search className="h-4 w-4" />}
          />
        </div>
      </div>
      
      {/* Create User Form */}
      <PermissionGate resource="users" action="create">
        <Card>
          <form onSubmit={handleCreateUser} className="p-6">
            <h3 className="text-lg font-semibold text-zinc-900 mb-4">Create New User</h3>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Input
                label="Email"
                type="email"
                required
                value={newUser.email}
                onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
              />
              <Input
                label="Name"
                type="text"
                value={newUser.name}
                onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
              />
              <Input
                label="Password"
                type="password"
                required
                value={newUser.password}
                onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
              />
              <div className="sm:col-span-2">
                <label className="block text-sm font-medium text-zinc-700 mb-2">Roles</label>
                {rolesLoading ? (
                  <div className="block w-full px-3 py-2 border border-zinc-300 rounded-lg flex justify-center">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-zinc-900"></div>
                  </div>
                ) : availableRoles.length === 0 ? (
                  <div className="block w-full px-3 py-2 border border-zinc-300 rounded-lg text-zinc-500">
                    No roles available
                  </div>
                ) : (
                  <div className="space-y-2 p-3 border border-zinc-300 rounded-lg max-h-96 overflow-y-auto">
                    {availableRoles.map((role) => {
                      const isChecked = newUser.roles.includes(role.name);
                      const isExpanded = expandedRoles.has(role.roleId);
                      const groupedPerms = role.permissions ? groupPermissions(role.permissions) : {};
                      const hasPermissions = role.permissions && role.permissions.length > 0;

                      return (
                        <div
                          key={role.roleId}
                          className={`rounded-lg border transition-colors ${
                            isChecked ? 'border-primary-300 bg-primary-50' : 'border-zinc-200 hover:border-zinc-300'
                          }`}
                        >
                          <label className="flex items-start p-3 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={isChecked}
                              onChange={(e) => {
                                const updatedRoles = e.target.checked
                                  ? [...newUser.roles, role.name]
                                  : newUser.roles.filter(r => r !== role.name);
                                setNewUser({ ...newUser, roles: updatedRoles });
                              }}
                              className="mt-1 h-4 w-4 text-primary-600 border-zinc-300 rounded focus:ring-primary-500"
                            />
                            <div className="ml-3 flex-1">
                              <div className="flex items-center justify-between">
                                <span className="text-sm font-medium text-zinc-900">
                                  {role.name.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                </span>
                                {hasPermissions && (
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      toggleRoleExpansion(role.roleId);
                                    }}
                                    className="ml-2 text-zinc-400 hover:text-zinc-600"
                                  >
                                    {isExpanded ? (
                                      <ChevronUp className="h-4 w-4" />
                                    ) : (
                                      <ChevronDown className="h-4 w-4" />
                                    )}
                                  </button>
                                )}
                              </div>
                              <p className="text-xs text-zinc-500 mt-1">{role.description}</p>
                            </div>
                          </label>

                          {/* Expanded permissions section */}
                          {isExpanded && hasPermissions && (
                            <div className="px-3 pb-3 ml-7">
                              <div className="bg-zinc-50 rounded-md p-2 space-y-1">
                                {Object.entries(groupedPerms).map(([category, actions]) => (
                                  <div key={category} className="text-xs">
                                    <span className="font-medium text-zinc-700">{category}:</span>{' '}
                                    <span className="text-zinc-600">{actions.join(', ')}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
                {newUser.roles.length === 0 && (
                  <p className="mt-1 text-sm text-zinc-500">Select at least one role</p>
                )}
              </div>
            </div>
            <div className="mt-4">
              <Button
                type="submit"
                disabled={newUser.roles.length === 0}
                leftIcon={<Plus className="h-4 w-4" />}
              >
                Create User
              </Button>
            </div>
          </form>
        </Card>
      </PermissionGate>

      {/* Error and Success Messages */}
      {error && (
        <Card className="border-red-200 bg-red-50">
          <div className="p-4 text-red-700">{error}</div>
        </Card>
      )}
      {success && (
        <Card className="border-green-200 bg-green-50">
          <div className="p-4 text-green-700">{success}</div>
        </Card>
      )}

      {/* Reset Password Modal */}
      <Modal open={!!resetPasswordUser} onOpenChange={(open) => !open && setResetPasswordUser(null)}>
        <ModalContent>
          <ModalHeader>
            <ModalTitle>Reset Password</ModalTitle>
          </ModalHeader>
          <ModalBody className="space-y-4">
            <Input
              label="New Password"
              type="password"
              value={resetPasswordUser?.newPassword || ''}
              onChange={(e) => resetPasswordUser && setResetPasswordUser({
                ...resetPasswordUser,
                newPassword: e.target.value,
              })}
            />
            <Input
              label="Confirm New Password"
              type="password"
              value={resetPasswordUser?.confirmPassword || ''}
              onChange={(e) => resetPasswordUser && setResetPasswordUser({
                ...resetPasswordUser,
                confirmPassword: e.target.value,
              })}
            />
          </ModalBody>
          <ModalFooter>
            <Button
              variant="secondary"
              onClick={() => setResetPasswordUser(null)}
            >
              Cancel
            </Button>
            <Button
              onClick={() => resetPasswordUser && handleResetPassword(resetPasswordUser.id)}
            >
              Reset Password
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* User List */}
      <Card className="overflow-hidden">
        <ul className="divide-y divide-zinc-200">
          {users.map((user) => (
            <li key={user.id}>
              <div className="px-4 py-4 flex items-center justify-between sm:px-6 hover:bg-zinc-50 transition-colors">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-primary-600 truncate">{user.email}</p>
                  <div className="mt-1 flex flex-wrap items-center gap-2">
                    {user.name && <span className="text-sm text-zinc-500">{user.name}</span>}
                    <Badge variant={user.status === 'active' ? 'success' : 'default'}>
                      {user.status}
                    </Badge>
                    {user.roles.map((role) => (
                      <Badge key={role} variant="primary" size="sm">
                        {role.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                      </Badge>
                    ))}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <PermissionGate resource="users" action="update">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setEditingUser(user)}
                      leftIcon={<Pencil className="h-4 w-4" />}
                    >
                      Edit
                    </Button>
                  </PermissionGate>
                  <PermissionGate resource="users" action="delete">
                    <Button
                      variant="danger"
                      size="sm"
                      onClick={() => setDeleteConfirmUser(user)}
                    >
                      Delete
                    </Button>
                  </PermissionGate>
                  <ActionMenu
                    sections={[
                      {
                        items: [
                          {
                            label: 'Reset Password',
                            icon: <Key className="h-4 w-4" />,
                            onClick: () => setResetPasswordUser({ id: user.id, oldPassword: '', newPassword: '', confirmPassword: '' }),
                            permission: { resource: 'users', action: 'update' },
                          },
                        ],
                      },
                      {
                        title: 'Account',
                        items: user.status === 'active'
                          ? [
                              {
                                label: 'Deactivate',
                                icon: <UserX className="h-4 w-4" />,
                                onClick: () => handleUserAction(user.id, 'deactivate'),
                                permission: { resource: 'users', action: 'update' },
                              },
                            ]
                          : [
                              {
                                label: 'Reactivate',
                                icon: <UserCheck className="h-4 w-4" />,
                                onClick: () => handleUserAction(user.id, 'reactivate'),
                                permission: { resource: 'users', action: 'update' },
                              },
                            ],
                      },
                    ] as ActionMenuSection[]}
                  />
                </div>
              </div>
            </li>
          ))}
        </ul>
      </Card>

      {/* User Edit Modal */}
      {editingUser && (
        <UserEditModal
          user={editingUser}
          roles={availableRoles}
          allRoles={allRoles}
          rolesLoading={rolesLoading}
          isOpen={!!editingUser}
          onClose={() => setEditingUser(null)}
          onSave={(updatedUser) => handleUpdateUser(updatedUser as User)}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={!!deleteConfirmUser}
        title="Move to Recycle Bin"
        message={`Move "${deleteConfirmUser?.email}" to the recycle bin? They can be restored within 30 days.`}
        confirmLabel="Move to Trash"
        onConfirm={async () => {
          if (deleteConfirmUser) {
            await handleUserAction(deleteConfirmUser.id, 'delete');
            setDeleteConfirmUser(null);
          }
        }}
        onCancel={() => setDeleteConfirmUser(null)}
      />
    </div>
  );
};

export default UserManagement;
